import { useCallback, useLayoutEffect, useRef, useState } from "react";

export function useCandidateToolbarOverflow(total: number) {
  const rowRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const fixedItems = useRef(new Set<HTMLDivElement>());
  const [visibleCount, setVisibleCount] = useState(total);

  const registerFixed = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;
    fixedItems.current.add(element);
    return () => {
      fixedItems.current.delete(element);
    };
  }, []);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const more = moreRef.current;
    if (!row || !more || typeof ResizeObserver === "undefined") return;

    const measure = () => {
      const itemWidth = more.offsetWidth;
      if (itemWidth <= 0) return;
      const gap = Number.parseFloat(getComputedStyle(row).columnGap) || 0;
      const fixed = Array.from(fixedItems.current);
      const fixedWidth = fixed.reduce((sum, element) => sum + element.offsetWidth, 0);
      const free = row.clientWidth - fixedWidth - itemWidth - fixed.length * gap;
      const fitting = Math.floor(free / (itemWidth + gap));
      setVisibleCount(Math.max(0, Math.min(total, fitting)));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(more);
    fixedItems.current.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [total]);

  return { rowRef, moreRef, registerFixed, visibleCount };
}
